import { useCallback, useEffect, useMemo, useRef, useState, type ComponentProps } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Search as SearchIcon, SlidersHorizontal } from 'lucide-react';
import axios from 'axios';
import {
  TrackingAd,
  AdSlot,
  TrackingAdProvider,
  useTrackingAdContext,
  type GoogleAdsPlacement,
} from '@low6-pickem/ui';
import { useLobby } from '../api/getLobby';
import type {
  LobbyGame,
  LobbyImageType,
  LobbyResponse,
  LobbySectionResponse,
} from '../api/getLobby';
import { useGetTenant, type TenantConfig } from '../api/getTenant';
import LobbyFilterView, {
  DEFAULT_LOBBY_FILTERS,
  type LobbyFilters,
} from '../components/LobbyFilterView';
import LobbyGameCard from '../components/LobbyGameCard';
// import LobbyGameSplashView from '../components/LobbyGameSplashView';
import LobbyMyGamesView from '../components/LobbyMyGamesView';
import LobbyPrimaryTabs from '../components/LobbyPrimaryTabs';
import { buildLobbyViewPath, isLobbyMyGamesPath, withSearch } from '../utils/lobbyViewPath';
import { MY_GAMES_TAB_PARAM, parseMyGamesTab, type MyGamesTab } from '../utils/lobbyMyGamesTabs';
import {
  applyLobbyPipeline,
  deriveLobbyFilterOptions,
  matchesLobbyModalFilters,
} from '../utils/lobbyFilterGames';
import { formatWinPrize, gameTypeLabel } from '../utils/lobbyGameDisplay';
import { collectDisplayedLobbyGames } from '../utils/lobbyDisplayedGames';
import {
  isDailyDrawAllowed,
  isLobbyGameVisible,
  isSectionLobbyGameEligible,
  isSquaresAllowed,
  parseShowDailyDrawParam,
  parseShowSquaresParam,
  type LobbyEmbedPermissions,
} from '../utils/lobbyDailyDrawPermission';
import { isCustomLobbyGame } from '../utils/lobbyPlayableStatus';
import {
  resolveSectionGamesClient,
  sectionUsesClientResolvableRule,
} from '../utils/lobbySectionClientResolve';
import { normalizeLobbyCardMetadataOptions } from '../utils/lobbySectionMetadata';
import { resolveGameIdFromUrl } from '@low6-pickem/frontend-shared';
import { resolveLobbyIdentifier } from '../utils/lobbyIdentifier';
import { buildPlayUrl } from '../utils/playUrl';
import { resolveSquaresLobbyGameRawPlayUrl } from '../utils/squaresPlayUrl';
import { ensureDailyRevealContestUrl } from '../utils/lobbyMyGamesNavigation';

interface Props {
  tenantSlug: string;
}

/**
 * Wraps lobby content in TrackingAdProvider when GAM config is available.
 * Children render immediately — ads attach once tenant config resolves.
 */
function LobbyTrackingAdProvider({
  tenant,
  lobbySlug,
  isLive,
  children,
}: {
  tenant: TenantConfig | undefined;
  lobbySlug: string;
  isLive: boolean;
  children: React.ReactNode;
}) {
  const networkId = tenant?.gamNetworkId ?? '';
  const enabled = Boolean(tenant?.gamEnabled && networkId);

  return (
    <TrackingAdProvider
      networkId={networkId}
      basePath="free-to-play/lobby"
      enabled={enabled}
      defaultTargeting={{
        s1: tenant?.code ?? '',
        s2: `${lobbySlug}`,
        s6: isLive ? 'True' : 'False',
      }}
    >
      {children}
    </TrackingAdProvider>
  );
}

/**
 * Wraps a LobbyGameCard in an invisible GAM impression-tracking slot, supplying
 * the s3–s4 slot-level targeting (section name, image size). The provider
 * supplies s1 (tenant slug) and s2 (lobby slug), and TrackingAd derives s5 from
 * the game id.
 *
 * The same game can appear in multiple sections (hero/action header/popular/
 * recently played/section rows), so each placement must pass a page-unique
 * `slotId` to avoid duplicate GPT slot div ids.
 */
function TrackedGameCard({
  sectionName,
  slotId,
  ...cardProps
}: ComponentProps<typeof LobbyGameCard> & {
  sectionName: string;
  slotId: string;
}) {
  return (
    <TrackingAd
      id={cardProps.game.pickem_id}
      divId={`tracking-ad-${slotId}`}
      targeting={{ s3: sectionName, s4: cardProps.displaySlot }}
    >
      <LobbyGameCard {...cardProps} />
    </TrackingAd>
  );
}

/** Banner sizes requested by a lobby `gam` section ad slot. */
const GAM_LOBBY_SIZES: [number, number][] = [
  [320, 50],
  [728, 90],
];

/** Maximum number of `s*` targeting keys a placement path may contribute. */
const GAM_MAX_TARGETING_PARTS = 5;

/**
 * How long a `gam` section waits for a creative once its slot is live, before it
 * collapses. Long enough for the ad request to round-trip on a phone connection.
 */
const GAM_EMPTY_COLLAPSE_MS = 2500;

/**
 * How long a `gam` section waits for the tenant's ad config and gpt.js to arrive
 * before giving up. Longer than the fill timeout because it covers a cold script
 * load; a blocked or missing gpt.js only ever resolves by hitting this deadline.
 */
const GAM_READY_TIMEOUT_MS = 6000;

/**
 * Renders a real Google Ad Manager banner for a `gam` lobby section. The ad-unit
 * path is derived from the provider's network id and the section's placement_id;
 * nothing is rendered until GPT is enabled and ready, or if no placement is set.
 *
 * `onEmpty` fires once it is clear no ad will appear, so the lobby can drop the
 * section entirely rather than leaving a blank gap where the banner would be.
 */
function LobbyGamSlot({
  section,
  isLive,
  onEmpty,
}: {
  section: LobbySectionResponse;
  isLive: boolean;
  onEmpty: (sectionId: string) => void;
}) {
  const { networkId, enabled, ready } = useTrackingAdContext();
  const placementId = (section.payload as { placement_id?: string }).placement_id?.trim();
  // Stable identity, so the collapse timers below are not restarted by every
  // re-render of the lobby.
  const reportEmpty = useCallback(() => onEmpty(section.id), [onEmpty, section.id]);

  // Split into path segments, dropping empties produced by leading/trailing or
  // doubled slashes so we never emit blank targeting values or an unbounded
  // number of `s*` keys.
  const parts = placementId ? placementId.split('/').filter(Boolean) : [];
  // Without a usable placement_id the section is misconfigured, so no amount of
  // waiting will ever fill it.
  const hasValidPlacement = parts.length > 0;
  const canRender = enabled && ready && Boolean(networkId) && hasValidPlacement;

  useEffect(() => {
    if (hasValidPlacement) return;
    if (placementId) {
      console.warn(
        '[Lobby] Skipping gam section with invalid placement_id:',
        section.id,
        placementId
      );
    }
    reportEmpty();
  }, [hasValidPlacement, placementId, section.id, reportEmpty]);

  // The tenant's ad config and gpt.js both arrive asynchronously, so an unready
  // slot is not yet proof that no ad is coming — wait out the deadline before
  // collapsing, rather than closing the gap on a slow load.
  useEffect(() => {
    if (!hasValidPlacement || canRender) return;
    const timer = setTimeout(reportEmpty, GAM_READY_TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [hasValidPlacement, canRender, reportEmpty]);

  if (!canRender) return null;

  const normalizedPlacementId = parts.join('/');

  const targeting = {
    ...Object.fromEntries(
      parts.slice(0, GAM_MAX_TARGETING_PARTS).map((value, index) => [`s${index + 1}`, value])
    ),
    s6: isLive ? 'True' : 'False',
  };

  const adPlacement: GoogleAdsPlacement = {
    placementKey: 'BODY',
    enabled: true,
    adUnitPath: `/${networkId}/${normalizedPlacementId}`,
    sizes: GAM_LOBBY_SIZES,
    targeting,
  };

  return (
    <AdSlot
      divId={`gam-${section.id}`}
      adPlacement={adPlacement}
      placement={normalizedPlacementId}
      reserveMinHeight={null}
      className="min-h-[50px] md:min-h-[90px]"
      onEmpty={reportEmpty}
      emptyTimeoutMs={GAM_EMPTY_COLLAPSE_MS}
    />
  );
}

/** Collapse space-y-8 gap when a section sits next to a GAM ad slot. */
function lobbySectionTightSpacingClass(
  index: number,
  kind: LobbySectionResponse['kind'],
  prevKind: LobbySectionResponse['kind'] | undefined
): string {
  if (index > 0 && (kind === 'gam' || prevKind === 'gam')) {
    return '-mt-8';
  }
  return '';
}

type GameFilter = 'all' | 'squares' | 'daily_draw' | 'pickem';

const GAME_FILTERS: Array<{ id: GameFilter; label: string }> = [
  { id: 'all', label: 'For You' },
  { id: 'squares', label: 'Squares' },
  { id: 'daily_draw', label: 'Daily Draw' },
  { id: 'pickem', label: "Pick'em" },
];

const IMAGE_DIMENSIONS: Record<LobbyImageType, { width: number; height: number }> = {
  large: { width: 361, height: 179 },
  medium: { width: 257, height: 144 },
  small: { width: 96, height: 96 },
  list_item: { width: 96, height: 96 },
  arcade_header: { width: 361, height: 440 },
};

interface ResolvedLobbyImage {
  sport: string;
  gameType: string;
  locale: string;
  imageType: LobbyImageType;
  imageUrl: string;
  displayWidth: number;
  displayHeight: number;
}

function normalizedGameType(type: string | null): Exclude<GameFilter, 'all'> | '' {
  if (!type) return '';
  if (type === 'daily_reveal' || type === 'daily_draw') return 'daily_draw';
  if (type === 'pickem' || type === 'squares') return type;
  return '';
}

function normalizedConfigGameType(type: string | null | undefined): string {
  if (!type) return 'pickem';
  const normalized = type.trim().toLowerCase();
  if (normalized === 'daily_reveal' || normalized === 'daily_draw') return 'daily_reveal';
  if (normalized === 'pickem' || normalized === 'squares') return normalized;
  return normalized;
}

function normalizedSport(sport: string | null | undefined): string {
  if (!sport) return 'all';
  return sport
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '_')
    .replace(/[^a-z0-9_-]/g, '');
}

function resolveGamePlayUrl(game: LobbyGame, lobbyGames: LobbyGame[]): string | null {
  const rawUrl = resolveSquaresLobbyGameRawPlayUrl(game, lobbyGames);
  // Daily Reveal tiles must name their own contest, or the game resolves the
  // globally active week and opens a different contest than the tile (DKPLAT-763).
  const contestUrl =
    game.game_type === 'squares'
      ? rawUrl
      : ensureDailyRevealContestUrl({ ...game, public_url: rawUrl });
  return buildPlayUrl(contestUrl);
}

function readString(record: Record<string, unknown>, keys: string[]): string | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string') {
      const trimmed = value.trim();
      if (trimmed) return trimmed;
    }
  }
  return null;
}

function readNumber(record: Record<string, unknown>, keys: string[]): number | null {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'number' && Number.isFinite(value)) return value;
  }
  return null;
}

function toLobbyImageType(value: string | null): LobbyImageType | null {
  if (!value) return null;
  if (value === 'large') return 'large';
  if (value === 'medium') return 'medium';
  if (value === 'small') return 'small';
  if (value === 'list_item') return 'list_item';
  if (value === 'arcade_header') return 'arcade_header';
  return null;
}

function collectLobbyImages(data: LobbyResponse): ResolvedLobbyImage[] {
  const imagesByKey = new Map<string, ResolvedLobbyImage>();

  const addImage = (
    imageInput: unknown,
    fallbackSport: string | null = null,
    fallbackGameType: string | null = null
  ) => {
    if (!imageInput || typeof imageInput !== 'object') return;

    const image = imageInput as Record<string, unknown>;
    const imageType = toLobbyImageType(readString(image, ['image_type', 'imageType']));
    const imageUrl = readString(image, ['image_url', 'imageUrl']);
    if (!imageType || !imageUrl) return;

    const locale = (readString(image, ['locale']) ?? 'en').toLowerCase();
    const sport = normalizedSport(readString(image, ['sport']) ?? fallbackSport ?? 'all');
    const gameType = normalizedConfigGameType(
      readString(image, ['game_type', 'gameType']) ?? fallbackGameType ?? 'pickem'
    );
    const displayWidth =
      readNumber(image, ['display_width', 'displayWidth']) ?? IMAGE_DIMENSIONS[imageType].width;
    const displayHeight =
      readNumber(image, ['display_height', 'displayHeight']) ?? IMAGE_DIMENSIONS[imageType].height;

    const key = `${sport}:${gameType}:${locale}:${imageType}`;
    imagesByKey.set(key, {
      sport,
      gameType,
      locale,
      imageType,
      imageUrl,
      displayWidth,
      displayHeight,
    });
  };

  if (Array.isArray(data.lobby.images)) {
    for (const image of data.lobby.images) {
      addImage(image);
    }
  }

  if (Array.isArray(data.lobby.configs)) {
    for (const configInput of data.lobby.configs) {
      if (!configInput || typeof configInput !== 'object') continue;
      const config = configInput as Record<string, unknown>;
      const sport = readString(config, ['sport']) ?? 'all';
      const gameType = readString(config, ['game_type', 'gameType']) ?? 'pickem';
      const configImages = config.images;
      if (!Array.isArray(configImages)) continue;
      for (const image of configImages) {
        addImage(image, sport, gameType);
      }
    }
  }

  return Array.from(imagesByKey.values());
}

function assetSourceMatches(
  imageSport: string,
  imageGameType: string,
  candidateSport: string,
  candidateGameType: string
): boolean {
  const sportOk = candidateSport === 'all' || imageSport === 'all' || imageSport === candidateSport;
  const typeOk =
    candidateGameType === 'all' || imageGameType === 'all' || imageGameType === candidateGameType;
  return sportOk && typeOk;
}

function findConfigImage(
  images: ResolvedLobbyImage[],
  sport: string,
  gameType: string,
  imageType: LobbyImageType
): ResolvedLobbyImage | null {
  const slotFallbacks: LobbyImageType[] =
    imageType === 'list_item'
      ? ['list_item', 'small']
      : imageType === 'arcade_header'
        ? ['arcade_header', 'large']
        : [imageType];

  const priority: Array<{ sport: string; gameType: string }> = [
    { sport, gameType },
    { sport: 'all', gameType },
    { sport, gameType: 'all' },
    { sport: 'all', gameType: 'all' },
    { sport, gameType: 'pickem' },
    { sport: 'all', gameType: 'pickem' },
  ];

  for (const slot of slotFallbacks) {
    for (const candidate of priority) {
      const exact = images.find(
        image =>
          image.imageType === slot &&
          assetSourceMatches(image.sport, image.gameType, candidate.sport, candidate.gameType) &&
          image.locale === 'en'
      );
      if (exact) return exact;

      const anyLocale = images.find(
        image =>
          image.imageType === slot &&
          assetSourceMatches(image.sport, image.gameType, candidate.sport, candidate.gameType)
      );
      if (anyLocale) return anyLocale;
    }
  }

  return null;
}

function gameImageBySlot(
  game: LobbyGame,
  imageType: LobbyImageType,
  lobbyImages: ResolvedLobbyImage[],
  fallback: string | null
): string | null {
  const configImage = findConfigImage(
    lobbyImages,
    normalizedSport(game.sport),
    normalizedConfigGameType(game.game_type),
    imageType
  );

  if (configImage?.imageUrl) return configImage.imageUrl;
  // When lobby/template assets are configured, do not fall back to per-game prize images (stale after template updates).
  if (lobbyImages.length > 0) return fallback;
  return game.text_prize_image_en || game.text_prize_image_fr || fallback;
}

function aspectStyle(type: LobbyImageType): { aspectRatio: string } {
  const size = IMAGE_DIMENSIONS[type];
  return { aspectRatio: `${size.width} / ${size.height}` };
}

/** Row layout slot only; list_item is never selected here — list assets are resolved separately for thumbnails/search. */
function sectionDisplaySlot(payload: Record<string, unknown> | undefined): LobbyImageType {
  const v = readString(payload ?? {}, ['display_asset_type']);
  if (v === 'large' || v === 'medium' || v === 'small' || v === 'arcade_header') return v;
  return 'medium';
}

/**
 * Per-section custom asset override (DKPLAT-521). When an admin sets a custom
 * asset on a lobby section it replaces the default sport/game-type asset for
 * every card in that section, so single-tile / pinned sections can show
 * matchup-specific creative. Returns null when unset so callers fall back to the
 * normal per-contest resolution.
 */
function sectionCustomAssetUrl(payload: Record<string, unknown> | undefined): string | null {
  return readString(payload ?? {}, ['custom_asset_url']);
}

function sectionGamesRowCardClass(slot: LobbyImageType, href: string | null): string {
  const base = 'group shrink-0 border border-white/10';
  const hover = href ? 'hover:border-green-400/60' : 'pointer-events-none opacity-70';
  switch (slot) {
    case 'arcade_header':
      return `${base} block w-full max-w-[361px] rounded-3xl border-[#7d4cff]/50 bg-[#10151d] shadow-[0_0_0_1px_rgba(125,76,255,0.35),0_0_24px_rgba(84,44,255,0.35)] ${hover}`;
    case 'large':
      return `${base} block w-[361px] rounded-2xl bg-[#12161f] ${hover}`;
    case 'medium':
      return `${base} block w-[257px] rounded-2xl bg-[#12161f] ${hover}`;
    case 'small':
      return `${base} block w-[96px] rounded-xl bg-[#12161f] ${hover}`;
    default:
      return `${base} block w-[257px] rounded-2xl bg-[#12161f] ${hover}`;
  }
}

export default function LobbyView({ tenantSlug }: Props) {
  const { lobbySlug } = useParams<{ lobbySlug?: string }>();

  /*
   * DK's iframe deep-link (DKPLAT-467). Every game is loaded from one host as
   * `/<segment>?tenantId=<code>&gameId=<guid>`, and that URL is configured on DK's side and
   * cannot change — so on `/lobby` the `gameId` param carries a LOBBY id. Read with the same
   * shared resolver the other three games use rather than a second reading of the query string.
   *
   * Their proxy does not hand us `/lobby` bare: it echoes the tenant code back as the path
   * segment, so we are entered at `/tenant/dk/lobby/dk?...&gameId=<lobby guid>`. That segment is
   * not a request for a specific lobby (and `dk` is the default lobby's own slug), so the id wins
   * there. A real `/lobby/<slug>` in the path still wins over a stray `gameId` — see
   * resolveLobbyIdentifier for the full precedence.
   *
   * Used ONLY to fetch. Navigation below still prefers `data.lobby.slug`, so opening My Games
   * from a deep link produces `/lobby/<real-slug>/mygames` rather than putting a guid in the URL.
   */
  const lobbyIdentifier = resolveLobbyIdentifier(lobbySlug, resolveGameIdFromUrl(), tenantSlug);
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data, isLoading, error } = useLobby(tenantSlug, lobbyIdentifier);
  // Prefetch tenant config in parallel with lobby data (used for GAM; does not block render).
  const { data: tenant } = useGetTenant(tenantSlug);

  const embedPermissions = useMemo((): LobbyEmbedPermissions => {
    return {
      showDailyDraw: parseShowDailyDrawParam(searchParams),
      showSquares: parseShowSquaresParam(searchParams),
    };
  }, [searchParams]);

  const searchInputRef = useRef<HTMLInputElement>(null);

  const [searchQuery, setSearchQuery] = useState('');
  const [isSearchViewOpen, setIsSearchViewOpen] = useState(false);
  const [isFilterViewOpen, setIsFilterViewOpen] = useState(false);
  const [lobbyFilters, setLobbyFilters] = useState<LobbyFilters>(DEFAULT_LOBBY_FILTERS);
  const [activeFilter, setActiveFilter] = useState<GameFilter>('all');
  // const [splashGame, setSplashGame] = useState<LobbyGame | null>(null);

  /** Driven by URL: `/lobby/:slug/mygames` (and `/mygames` for the tenant root route). */
  const isMyGamesViewOpen = isLobbyMyGamesPath(location.pathname);

  const openMyGames = useCallback(() => {
    const slug = lobbySlug ?? data?.lobby.slug;
    navigate(withSearch(buildLobbyViewPath(slug, 'mygames'), location.search));
  }, [data?.lobby.slug, lobbySlug, location.search, navigate]);

  const closeMyGames = useCallback(() => {
    const slug = lobbySlug ?? data?.lobby.slug;
    // Drop the My Games sub-tab param — it means nothing on the lobby tab, and leaving it
    // behind would restore a stale sub-tab the next time My Games is opened.
    const search = new URLSearchParams(location.search);
    search.delete(MY_GAMES_TAB_PARAM);
    navigate(withSearch(buildLobbyViewPath(slug), search.toString()));
  }, [data?.lobby.slug, lobbySlug, location.search, navigate]);

  /**
   * The My Games sub-tab lives in the URL (`?tab=live`) so a tab is shareable and survives a
   * reload. It replaces rather than pushes: Back should leave My Games, not walk back through
   * every sub-tab the user touched.
   */
  const myGamesTab = parseMyGamesTab(searchParams.get(MY_GAMES_TAB_PARAM));

  const handleMyGamesTabChange = useCallback(
    (tab: MyGamesTab) => {
      const next = new URLSearchParams(searchParams);
      next.set(MY_GAMES_TAB_PARAM, tab);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const handlePrimaryTabSelect = useCallback(
    (tab: 'lobby' | 'mygames') => {
      if (tab === 'mygames') openMyGames();
      else closeMyGames();
    },
    [closeMyGames, openMyGames]
  );

  const lobbyImages = useMemo(() => {
    if (!data) return [];
    return collectLobbyImages(data);
  }, [data]);

  const visibleGames = useMemo(
    () => data?.games.filter(game => isLobbyGameVisible(game, embedPermissions)) ?? [],
    [data?.games, embedPermissions]
  );

  /** Category chips only — search is a separate full-screen view and does not filter the main lobby. */
  const gameMatchesCategoryFilter = useCallback(
    (game: LobbyGame) =>
      activeFilter === 'all' || normalizedGameType(game.game_type) === activeFilter,
    [activeFilter]
  );

  const lobbyFilteredGames = useMemo(
    () => applyLobbyPipeline(visibleGames, lobbyFilters, gameMatchesCategoryFilter),
    [visibleGames, lobbyFilters, gameMatchesCategoryFilter]
  );

  const hasSectionLayout = Boolean(data?.sections && data.sections.length > 0);

  const sectionDiscoveryPool = useMemo(() => {
    const pool = data?.tenant_games ?? data?.games ?? [];
    const seen = new Set<string>();
    const out: LobbyGame[] = [];
    for (const game of pool) {
      if (seen.has(game.pickem_id)) continue;
      if (!isSectionLobbyGameEligible(game, embedPermissions)) continue;
      seen.add(game.pickem_id);
      out.push(game);
    }
    return out;
  }, [data?.tenant_games, data?.games, embedPermissions]);

  const sectionBlocks = useMemo(() => {
    if (!data?.sections?.length) return [];

    return data.sections.map((sec: LobbySectionResponse) => {
      const resolvedGames = sectionUsesClientResolvableRule(sec)
        ? resolveSectionGamesClient(sec, sectionDiscoveryPool)
        : (sec.games ?? []);

      return {
        ...sec,
        games: resolvedGames.filter(
          g =>
            isSectionLobbyGameEligible(g, embedPermissions) &&
            gameMatchesCategoryFilter(g) &&
            matchesLobbyModalFilters(g, lobbyFilters)
        ),
      };
    });
  }, [
    data?.sections,
    sectionDiscoveryPool,
    gameMatchesCategoryFilter,
    lobbyFilters,
    embedPermissions,
  ]);

  // `gam` sections that reported no ad. They are dropped from the rendered list
  // so the lobby closes up instead of showing a blank banner-sized gap, and so
  // the neighbouring sections keep their normal spacing.
  const [emptyGamSectionIds, setEmptyGamSectionIds] = useState<ReadonlySet<string>>(
    () => new Set()
  );

  const markGamSectionEmpty = useCallback((sectionId: string) => {
    setEmptyGamSectionIds(prev => (prev.has(sectionId) ? prev : new Set(prev).add(sectionId)));
  }, []);

  const visibleSectionBlocks = useMemo(
    () => sectionBlocks.filter(sec => !(sec.kind === 'gam' && emptyGamSectionIds.has(sec.id))),
    [sectionBlocks, emptyGamSectionIds]
  );

  const hasAnyFeaturedAssignments = useMemo(
    () =>
      lobbyFilteredGames.some(
        game =>
          Boolean(game.featured_arcade_header) ||
          Boolean(game.featured_large) ||
          Boolean(game.featured_medium) ||
          Boolean(game.featured_small) ||
          Boolean(game.featured_list_item)
      ),
    [lobbyFilteredGames]
  );

  const heroCardGames = useMemo(() => {
    if (hasAnyFeaturedAssignments) {
      return lobbyFilteredGames.filter(game => Boolean(game.featured_arcade_header));
    }
    return lobbyFilteredGames.slice(0, 1);
  }, [lobbyFilteredGames, hasAnyFeaturedAssignments]);

  const actionHeaderGames = useMemo(() => {
    if (hasAnyFeaturedAssignments) {
      return lobbyFilteredGames.filter(game => Boolean(game.featured_large));
    }
    return lobbyFilteredGames.slice(0, 6);
  }, [lobbyFilteredGames, hasAnyFeaturedAssignments]);

  const popularSectionGames = useMemo(() => {
    if (hasAnyFeaturedAssignments) {
      return lobbyFilteredGames.filter(game => Boolean(game.featured_medium)).slice(0, 12);
    }
    return lobbyFilteredGames.slice(0, 12);
  }, [lobbyFilteredGames, hasAnyFeaturedAssignments]);

  const recentlyPlayedSectionGames = useMemo(() => {
    if (hasAnyFeaturedAssignments) {
      return lobbyFilteredGames
        .filter(game => Boolean(game.featured_list_item) || Boolean(game.featured_small))
        .slice(0, 16);
    }
    return lobbyFilteredGames.slice(0, 16);
  }, [lobbyFilteredGames, hasAnyFeaturedAssignments]);

  /** Same contest pool as the main lobby home (sections / featured rows), for search + Suggested. */
  const displayedLobbyGames = useMemo(
    () =>
      collectDisplayedLobbyGames({
        hasSectionLayout,
        sectionGames: sectionBlocks.flatMap(sec => sec.games ?? []),
        hasAnyFeaturedAssignments,
        heroCardGames,
        actionHeaderGames,
        popularSectionGames,
        recentlyPlayedSectionGames,
        fallbackGames: lobbyFilteredGames,
      }),
    [
      hasSectionLayout,
      sectionBlocks,
      hasAnyFeaturedAssignments,
      heroCardGames,
      actionHeaderGames,
      popularSectionGames,
      recentlyPlayedSectionGames,
      lobbyFilteredGames,
    ]
  );

  /** Contests on the lobby home before modal filters — drives filter modal options. */
  const gamesForFilterOptions = useMemo(() => {
    const categoryPass = gameMatchesCategoryFilter;
    const eligible = (game: LobbyGame) =>
      isLobbyGameVisible(game, embedPermissions) && categoryPass(game);

    if (hasSectionLayout) {
      const seen = new Set<string>();
      const out: LobbyGame[] = [];
      for (const sec of data?.sections ?? []) {
        const resolvedGames = sectionUsesClientResolvableRule(sec)
          ? resolveSectionGamesClient(sec, sectionDiscoveryPool)
          : (sec.games ?? []);
        for (const game of resolvedGames) {
          if (
            !isSectionLobbyGameEligible(game, embedPermissions) ||
            !categoryPass(game) ||
            seen.has(game.pickem_id)
          ) {
            continue;
          }
          seen.add(game.pickem_id);
          out.push(game);
        }
      }
      return out;
    }

    const pool = visibleGames.filter(eligible);
    const hasFeatured = pool.some(
      game =>
        Boolean(game.featured_arcade_header) ||
        Boolean(game.featured_large) ||
        Boolean(game.featured_medium) ||
        Boolean(game.featured_small) ||
        Boolean(game.featured_list_item)
    );

    return collectDisplayedLobbyGames({
      hasSectionLayout: false,
      sectionGames: [],
      hasAnyFeaturedAssignments: hasFeatured,
      heroCardGames: hasFeatured
        ? pool.filter(game => Boolean(game.featured_arcade_header))
        : pool.slice(0, 1),
      actionHeaderGames: hasFeatured
        ? pool.filter(game => Boolean(game.featured_large))
        : pool.slice(0, 6),
      popularSectionGames: hasFeatured
        ? pool.filter(game => Boolean(game.featured_medium)).slice(0, 12)
        : pool.slice(0, 12),
      recentlyPlayedSectionGames: hasFeatured
        ? pool
            .filter(game => Boolean(game.featured_list_item) || Boolean(game.featured_small))
            .slice(0, 16)
        : pool.slice(0, 16),
      fallbackGames: pool,
    });
  }, [
    data?.sections,
    sectionDiscoveryPool,
    gameMatchesCategoryFilter,
    hasSectionLayout,
    embedPermissions,
    visibleGames,
  ]);

  useEffect(() => {
    if (
      !isDailyDrawAllowed(embedPermissions.showDailyDraw ?? null) &&
      activeFilter === 'daily_draw'
    ) {
      setActiveFilter('all');
    }
    if (!isSquaresAllowed(embedPermissions.showSquares ?? null) && activeFilter === 'squares') {
      setActiveFilter('all');
    }
  }, [activeFilter, embedPermissions]);

  const { gameTypes: availableGameTypes, sports: availableSports } = useMemo(
    () => deriveLobbyFilterOptions(gamesForFilterOptions),
    [gamesForFilterOptions]
  );

  useEffect(() => {
    setLobbyFilters(prev => {
      const nextGameTypes = prev.gameTypes.filter(id => availableGameTypes.includes(id));
      const nextSports = prev.sports.filter(id => availableSports.includes(id));
      if (
        nextGameTypes.length === prev.gameTypes.length &&
        nextSports.length === prev.sports.length
      ) {
        return prev;
      }
      return { ...prev, gameTypes: nextGameTypes, sports: nextSports };
    });
  }, [availableGameTypes, availableSports]);

  const searchResultGames = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    const pool = displayedLobbyGames;
    if (!q) return pool;
    return pool.filter(game => {
      const haystack = [
        game.title_en,
        game.title_fr,
        game.prize_text_en,
        game.prize_text_fr,
        game.sport,
        gameTypeLabel(game.game_type),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [displayedLobbyGames, searchQuery]);

  const heroGame = heroCardGames[0] ?? lobbyFilteredGames[0] ?? null;

  const closeSearchView = useCallback(() => {
    setIsSearchViewOpen(false);
    setSearchQuery('');
  }, []);

  // Navigate straight into the game (no intermediate splash). In-app games use the lobby
  // iframe; custom external links must break out to the parent (sportsbook cannot be iframed).
  const handleSelectGame = useCallback(
    (game: LobbyGame) => {
      const playUrl = resolveGamePlayUrl(game, data?.games ?? []);
      if (!playUrl) return;
      if (isCustomLobbyGame(game)) {
        window.top!.location.href = playUrl;
        return;
      }
      window.location.href = playUrl;
    },
    [data?.games]
  );

  // Splash interstitial — disabled until product re-enables it.
  // const handleSelectGameSplash = useCallback((game: LobbyGame) => {
  //   setSplashGame(game);
  // }, []);

  useEffect(() => {
    if (!isSearchViewOpen) return;
    const timer = window.setTimeout(() => {
      searchInputRef.current?.focus();
    }, 0);
    return () => window.clearTimeout(timer);
  }, [isSearchViewOpen]);

  if (isLoading && !data) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="text-gray-400">Loading lobby...</div>
      </div>
    );
  }

  if (error && !data) {
    const is404 = axios.isAxiosError(error) && error.response?.status === 404;
    return (
      <div className="flex min-h-screen items-center justify-center p-6 text-center">
        <div>
          <h1 className="mb-2 text-2xl font-semibold">
            {is404 ? 'Lobby not found' : 'Lobby unavailable'}
          </h1>
          {is404 ? (
            <p className="text-gray-400">
              No{' '}
              {lobbyIdentifier ? (
                <>
                  lobby <code className="font-mono text-green-400">{lobbyIdentifier}</code>
                </>
              ) : (
                'default lobby'
              )}{' '}
              is configured for tenant{' '}
              <code className="font-mono text-green-400">{tenantSlug}</code>.
            </p>
          ) : (
            <p className="text-gray-400">
              We are retrying in the background. If needed, refresh once backend services are up.
            </p>
          )}
        </div>
      </div>
    );
  }

  if (!data) return null;

  const filterModal = (
    <LobbyFilterView
      isOpen={isFilterViewOpen}
      value={lobbyFilters}
      availableGameTypes={availableGameTypes}
      availableSports={availableSports}
      onClose={() => setIsFilterViewOpen(false)}
      onApply={next => {
        setLobbyFilters(next);
        setIsFilterViewOpen(false);
      }}
    />
  );

  // if (splashGame) {
  //   const logoUrl =
  //     gameImageBySlot(splashGame, 'list_item', lobbyImages, data?.lobby.banner_image_url ?? null) ??
  //     gameImageBySlot(splashGame, 'small', lobbyImages, data?.lobby.banner_image_url ?? null) ??
  //     splashGame.text_prize_image_en ??
  //     null;
  //   const backgroundUrl =
  //     gameImageBySlot(
  //       splashGame,
  //       'arcade_header',
  //       lobbyImages,
  //       data?.lobby.banner_image_url ?? null
  //     ) ??
  //     gameImageBySlot(splashGame, 'large', lobbyImages, data?.lobby.banner_image_url ?? null) ??
  //     null;
  //   const rawPlayUrl =
  //     splashGame.game_type === 'squares'
  //       ? buildSquaresContestPlayUrl(splashGame.pickem_id, data?.games ?? [])
  //       : splashGame.public_url;
  //   const playUrl = buildPlayUrl(rawPlayUrl);
  //
  //   return (
  //     <LobbyGameSplashView
  //       game={splashGame}
  //       logoUrl={logoUrl}
  //       backgroundUrl={backgroundUrl}
  //       playUrl={playUrl}
  //       onBack={() => setSplashGame(null)}
  //     />
  //   );
  // }

  if (isSearchViewOpen) {
    return (
      <LobbyTrackingAdProvider tenant={tenant} lobbySlug={data.lobby.slug} isLive={data.isLive}>
        <main className="flex min-h-screen flex-col bg-[#05070b]">
          <header className="sticky top-0 z-20 border-b border-white/10 bg-[#05070b]/95 px-3 py-3 backdrop-blur supports-[backdrop-filter]:bg-[#05070b]/80">
            <div className="mx-auto flex w-full max-w-[420px] items-center gap-2 md:max-w-6xl">
              <button
                type="button"
                onClick={closeSearchView}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/20 bg-black/45 text-white transition hover:border-white/40 hover:bg-black/55"
                aria-label="Back to lobby"
              >
                <ChevronLeft className="h-6 w-6" aria-hidden />
              </button>
              <label className="sr-only" htmlFor="lobby-game-search">
                Search contests
              </label>
              <div className="flex h-11 min-w-0 flex-1 items-center rounded-full border border-white/15 bg-black/45 px-3">
                <SearchIcon className="mr-2 h-5 w-5 shrink-0 text-white/75" aria-hidden />
                <input
                  ref={searchInputRef}
                  id="lobby-game-search"
                  type="search"
                  value={searchQuery}
                  onChange={event => setSearchQuery(event.target.value)}
                  onKeyDown={event => {
                    if (event.key === 'Escape') closeSearchView();
                  }}
                  placeholder='Search "Super Bowl"'
                  className="h-full min-w-0 flex-1 bg-transparent text-base text-white placeholder:text-gray-400 focus:outline-none"
                  autoComplete="off"
                  autoCorrect="off"
                />
              </div>
              <button
                type="button"
                onClick={() => setIsFilterViewOpen(true)}
                className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/20 bg-black/45 text-white/85 transition hover:border-white/40 hover:text-white"
                aria-label="Open game filters"
              >
                <SlidersHorizontal className="h-5 w-5" aria-hidden />
              </button>
            </div>
          </header>

          <div className="mx-auto flex w-full max-w-[420px] flex-1 flex-col px-3 pb-10 pt-4 md:max-w-6xl md:px-6">
            <h2 className="mb-3 text-lg font-bold text-white">Suggested</h2>
            {searchResultGames.length === 0 ? (
              <p className="text-sm text-gray-400">
                {searchQuery.trim()
                  ? 'No contests match your search. Try another keyword.'
                  : 'No contests match your current filters.'}
              </p>
            ) : (
              <ul className="divide-y divide-white/10">
                {searchResultGames.map(game => {
                  const href = resolveGamePlayUrl(game, data?.games ?? []);
                  const thumb =
                    gameImageBySlot(game, 'list_item', lobbyImages, data.lobby.banner_image_url) ??
                    game.text_prize_image_en;
                  const title = game.title_en || 'Untitled';
                  const subtitle =
                    formatWinPrize(game) ||
                    `Play ${gameTypeLabel(game.game_type) || 'now'} — win prizes.`;
                  const badgeParts = [gameTypeLabel(game.game_type), game.sport].filter(Boolean);
                  const badge = badgeParts.join(' · ') || 'Contest';

                  return (
                    <li key={game.pickem_id}>
                      <button
                        type="button"
                        onClick={() => handleSelectGame(game)}
                        className="flex w-full gap-3 py-3 text-left first:pt-0 hover:opacity-95 transition-opacity"
                      >
                        <div className="relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border border-white/10 bg-[#141922]">
                          {thumb ? (
                            <img src={thumb} alt={title} className="h-full w-full object-cover" />
                          ) : (
                            <div className="h-full w-full bg-gradient-to-br from-[#2a3645] to-[#0f1722]" />
                          )}
                        </div>
                        <div className="min-w-0 flex-1 pt-0.5">
                          <p className="font-semibold leading-tight text-white">{title}</p>
                          <p className="mt-1 line-clamp-2 text-sm leading-snug text-white/75">
                            {subtitle}
                          </p>
                          <span className="mt-2 inline-flex max-w-full rounded-full bg-white/10 px-2.5 py-1 text-[11px] font-medium leading-none text-gray-300">
                            {badge}
                          </span>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </main>
        {filterModal}
      </LobbyTrackingAdProvider>
    );
  }

  if (isMyGamesViewOpen) {
    return (
      <main className="min-h-screen bg-[#05070b] pb-10">
        <section className="mx-auto w-full max-w-[420px] px-3 py-5 md:max-w-6xl md:px-6 md:py-8">
          <LobbyPrimaryTabs active="mygames" onSelect={handlePrimaryTabSelect} />
          <LobbyMyGamesView
            lobbyGames={data?.games ?? []}
            activeTab={myGamesTab}
            onTabChange={handleMyGamesTabChange}
            onBrowseLobby={closeMyGames}
          />
        </section>
      </main>
    );
  }

  return (
    <LobbyTrackingAdProvider tenant={tenant} lobbySlug={data.lobby.slug} isLive={data.isLive}>
      <main className="min-h-screen bg-[#05070b] pb-12">
        <section className="mx-auto w-full max-w-[420px] px-3 py-5 md:max-w-6xl md:px-6 md:py-8">
          <LobbyPrimaryTabs active="lobby" onSelect={handlePrimaryTabSelect} />
          {visibleGames.length === 0 ? (
            <p className="py-12 text-center text-gray-500">
              No games are available in this lobby yet. Check back soon.
            </p>
          ) : (
            <>
              {/* Lobby header tracking — the impression slot fires whether or not
                  the header section itself is configured/displayed. The visible
                  section only renders when banner_image_url, header_title, and/or
                  header_subtitle are set. */}
              <TrackingAd
                id="lobby-header"
                targeting={{ s2: data.lobby.title, s3: 'Lobby Header' }}
              >
                {(data.lobby.banner_image_url ||
                  data.lobby.header_title ||
                  data.lobby.header_subtitle) && (
                  <section className="mb-4">
                    {data.lobby.banner_image_url && (
                      <img
                        src={data.lobby.banner_image_url}
                        alt={data.lobby.header_title ?? 'Lobby header'}
                        className="mx-auto block h-auto w-full max-w-[361px] object-contain"
                        loading="eager"
                      />
                    )}
                    {(data.lobby.header_title || data.lobby.header_subtitle) && (
                      <div className="mt-2 px-1 text-center">
                        {data.lobby.header_title && (
                          <h2 className="text-lg font-bold text-white">
                            {data.lobby.header_title}
                          </h2>
                        )}
                        {data.lobby.header_subtitle && (
                          <p className="text-sm text-white/70">{data.lobby.header_subtitle}</p>
                        )}
                      </div>
                    )}
                  </section>
                )}
              </TrackingAd>

              {/* Search icon — hidden for now */}
              {/* <section className="mb-4">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={() => setIsSearchViewOpen(true)}
                    className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full border border-white/20 bg-black/45 text-white/80 transition hover:border-white/40 hover:text-white"
                    aria-label="Open search"
                  >
                    <SearchIcon className="h-5 w-5" aria-hidden />
                  </button>
                </div>
              </section> */}

              {hasSectionLayout ? (
                visibleSectionBlocks.every(s => (s.games?.length ?? 0) === 0) &&
                !visibleSectionBlocks.some(s => s.kind === 'custom_banner' || s.kind === 'gam') ? (
                  <div className="mb-8 rounded-2xl border border-white/10 bg-black/20 px-5 py-7 text-center">
                    <p className="text-sm text-gray-300">No games match your current filters.</p>
                    <button
                      type="button"
                      onClick={() => setActiveFilter('all')}
                      className="mt-4 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-xs font-semibold text-gray-200 transition hover:bg-white/20"
                    >
                      Clear filters
                    </button>
                  </div>
                ) : (
                  <div className="space-y-8">
                    {visibleSectionBlocks.map((sec, index) => {
                      const prevKind =
                        index > 0 ? visibleSectionBlocks[index - 1]?.kind : undefined;
                      const tightSpacing = lobbySectionTightSpacingClass(index, sec.kind, prevKind);

                      if (sec.kind === 'custom_banner') {
                        const p = sec.payload as {
                          image_url?: string;
                          href?: string;
                          alt?: string;
                        };
                        if (!p.image_url?.trim()) return null;
                        const inner = (
                          <img
                            src={p.image_url}
                            alt={p.alt || sec.title || 'Banner'}
                            className="mx-auto block max-h-[200px] w-full max-w-[420px] rounded-2xl object-contain"
                          />
                        );
                        return (
                          <TrackingAd
                            key={sec.id}
                            id={sec.id}
                            targeting={{ s2: data.lobby.title, s3: sec.title ?? 'Custom Banner' }}
                          >
                            <section className={tightSpacing}>
                              {p.href?.trim() ? (
                                <a href={p.href} className="block">
                                  {inner}
                                </a>
                              ) : (
                                inner
                              )}
                            </section>
                          </TrackingAd>
                        );
                      }
                      if (sec.kind === 'gam') {
                        return (
                          <section key={sec.id} className={tightSpacing}>
                            <LobbyGamSlot
                              section={sec}
                              isLive={data.isLive}
                              onEmpty={markGamSectionEmpty}
                            />
                          </section>
                        );
                      }
                      if (!sec.games?.length) return null;
                      const displaySlot = sectionDisplaySlot(
                        sec.payload as Record<string, unknown>
                      );
                      const metadataOptions = normalizeLobbyCardMetadataOptions(
                        sec.payload as Record<string, unknown>
                      );
                      const customAssetUrl = sectionCustomAssetUrl(
                        sec.payload as Record<string, unknown>
                      );
                      return (
                        <section key={sec.id} className={tightSpacing}>
                          {sec.title ? (
                            <h3 className="mb-1 text-xl font-semibold text-white">{sec.title}</h3>
                          ) : null}
                          {sec.subtitle ? (
                            <p className="mb-3 text-sm text-gray-400">{sec.subtitle}</p>
                          ) : null}
                          <div className="no-scrollbar -mx-1 flex gap-3 overflow-x-auto px-1 pb-1">
                            {sec.games.map(game => {
                              const href = resolveGamePlayUrl(game, data?.games ?? []);
                              const image =
                                customAssetUrl ??
                                gameImageBySlot(
                                  game,
                                  displaySlot,
                                  lobbyImages,
                                  data.lobby.banner_image_url
                                );
                              return (
                                <TrackedGameCard
                                  key={`${sec.id}-${game.pickem_id}-${displaySlot}`}
                                  slotId={`${sec.id}-${game.pickem_id}-${displaySlot}`}
                                  sectionName={sec.title ?? ''}
                                  game={game}
                                  displaySlot={displaySlot}
                                  metadataOptions={metadataOptions}
                                  imageUrl={image}
                                  href={href}
                                  onClick={
                                    isCustomLobbyGame(game)
                                      ? undefined
                                      : () => handleSelectGame(game)
                                  }
                                  className={sectionGamesRowCardClass(displaySlot, href)}
                                  style={aspectStyle(displaySlot)}
                                />
                              );
                            })}
                          </div>
                        </section>
                      );
                    })}
                  </div>
                )
              ) : lobbyFilteredGames.length === 0 ? (
                <div className="mb-8 rounded-2xl border border-white/10 bg-black/20 px-5 py-7 text-center">
                  <p className="text-sm text-gray-300">No games match your current filters.</p>
                  <button
                    type="button"
                    onClick={() => setActiveFilter('all')}
                    className="mt-4 rounded-full border border-white/15 bg-white/10 px-4 py-1.5 text-xs font-semibold text-gray-200 transition hover:bg-white/20"
                  >
                    Clear filters
                  </button>
                </div>
              ) : (
                <>
                  {heroGame &&
                    (() => {
                      const heroHref = resolveGamePlayUrl(heroGame, data?.games ?? []);
                      const heroImage = gameImageBySlot(
                        heroGame,
                        'arcade_header',
                        lobbyImages,
                        data.lobby.banner_image_url
                      );

                      return (
                        <section className="mb-6">
                          <TrackedGameCard
                            slotId={`featured-${heroGame.pickem_id}-arcade_header`}
                            sectionName="Featured"
                            game={heroGame}
                            displaySlot="arcade_header"
                            imageUrl={heroImage}
                            href={heroHref}
                            onClick={
                              isCustomLobbyGame(heroGame)
                                ? undefined
                                : () => handleSelectGame(heroGame)
                            }
                            className={[
                              'group w-full max-w-[361px] rounded-3xl border border-[#7d4cff] bg-[#10151d]',
                              'shadow-[0_0_0_1px_rgba(125,76,255,0.5),0_0_28px_rgba(84,44,255,0.5)]',
                              heroHref
                                ? 'hover:-translate-y-0.5 hover:border-green-400/65'
                                : 'pointer-events-none opacity-70',
                            ].join(' ')}
                            style={aspectStyle('arcade_header')}
                          />
                        </section>
                      );
                    })()}

                  {actionHeaderGames.length > 0 && (
                    <section className="mb-6">
                      <div className="mb-3 flex items-center justify-between">
                        <h3 className="text-xl font-semibold text-white">Action Header</h3>
                      </div>
                      <div className="no-scrollbar -mx-1 flex gap-3 overflow-x-auto px-1 pb-1">
                        {actionHeaderGames.map(game => {
                          const href = resolveGamePlayUrl(game, data?.games ?? []);
                          const image = gameImageBySlot(
                            game,
                            'large',
                            lobbyImages,
                            data.lobby.banner_image_url
                          );

                          return (
                            <TrackedGameCard
                              key={`${game.pickem_id}-large`}
                              slotId={`action-header-${game.pickem_id}-large`}
                              sectionName="Action Header"
                              game={game}
                              displaySlot="large"
                              imageUrl={image}
                              href={href}
                              onClick={
                                isCustomLobbyGame(game) ? undefined : () => handleSelectGame(game)
                              }
                              className={sectionGamesRowCardClass('large', href)}
                              style={aspectStyle('large')}
                            />
                          );
                        })}
                      </div>
                    </section>
                  )}

                  <section className="mb-6">
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-xl font-semibold text-white">Popular Games</h3>
                    </div>
                    <div className="no-scrollbar -mx-1 flex gap-3 overflow-x-auto px-1 pb-1">
                      {popularSectionGames.map(game => {
                        const href = resolveGamePlayUrl(game, data?.games ?? []);
                        const image = gameImageBySlot(
                          game,
                          'medium',
                          lobbyImages,
                          data.lobby.banner_image_url
                        );

                        return (
                          <TrackedGameCard
                            key={`${game.pickem_id}-medium`}
                            slotId={`popular-${game.pickem_id}-medium`}
                            sectionName="Popular Games"
                            game={game}
                            displaySlot="medium"
                            imageUrl={image}
                            href={href}
                            onClick={
                              isCustomLobbyGame(game) ? undefined : () => handleSelectGame(game)
                            }
                            className={sectionGamesRowCardClass('medium', href)}
                            style={aspectStyle('medium')}
                          />
                        );
                      })}
                    </div>
                  </section>

                  <section>
                    <div className="mb-3 flex items-center justify-between">
                      <h3 className="text-xl font-semibold text-white">Recently Played</h3>
                    </div>
                    <div className="no-scrollbar -mx-1 flex gap-2 overflow-x-auto px-1 pb-1">
                      {recentlyPlayedSectionGames.map(game => {
                        const href = resolveGamePlayUrl(game, data?.games ?? []);
                        const image = gameImageBySlot(
                          game,
                          'list_item',
                          lobbyImages,
                          data.lobby.banner_image_url
                        );

                        return (
                          <TrackedGameCard
                            key={`${game.pickem_id}-list`}
                            slotId={`recently-played-${game.pickem_id}-list`}
                            sectionName="Recently Played"
                            game={game}
                            displaySlot="list_item"
                            imageUrl={image}
                            href={href}
                            onClick={
                              isCustomLobbyGame(game) ? undefined : () => handleSelectGame(game)
                            }
                            className={sectionGamesRowCardClass('small', href)}
                            style={{ width: 96, height: 96 }}
                          />
                        );
                      })}
                    </div>
                  </section>
                </>
              )}
            </>
          )}
        </section>
      </main>
      {filterModal}
    </LobbyTrackingAdProvider>
  );
}
